import { create } from 'zustand';
import { db } from '../firebase';
import { 
  doc, 
  setDoc, 
  getDoc,
  Timestamp, 
} from 'firebase/firestore';
import { handleFirestoreError, OperationType } from '../utils/firestoreErrorHandler';
import { 
  PlayerAchievementState,
  PlayerAchievementProgress
} from '../types';
import { evaluateAchievements } from '../services/achievementService';
import { useAuthStore } from './useAuthStore';
import { ACHIEVEMENTS } from '../constants/achievements';
import { toast } from 'sonner';

interface AchievementStore extends PlayerAchievementState {
  isLoading: boolean;
  isInitialized: boolean;
  lastEvaluated: number;
  fetchAchievements: (userId: string) => Promise<void>;
  evaluateAchievements: (userId: string) => Promise<void>;
  claimAchievement: (userId: string, achievementId: string) => Promise<void>;
  setActiveTitle: (userId: string, titleId: string | null) => Promise<void>;
  resetAchievements: (userId: string) => Promise<void>;
}

export const useAchievementStore = create<AchievementStore>((set, get) => ({
  achievements: {},
  unlockedBadgeIds: [],
  unlockedTitleIds: [],
  activeTitleId: null,
  isLoading: false,
  isInitialized: false,
  lastEvaluated: 0,

  fetchAchievements: async (userId: string) => {
    if (!userId) return;
    
    set({ isLoading: true });
    try {
      const achievementsRef = doc(db, 'users', userId, 'achievements', 'state');
      const snap = await getDoc(achievementsRef);
      
      if (snap.exists()) {
        const data = snap.data() as PlayerAchievementState;
        
        const currentAchievements = get().achievements;
        const newAchievements = data.achievements || {};
        const isInitialized = get().isInitialized;
        
        if (isInitialized) {
          Object.keys(newAchievements).forEach(id => {
            if (newAchievements[id].unlocked && !currentAchievements[id]?.unlocked) {
              const def = ACHIEVEMENTS.find(a => a.id === id);
              if (def) {
                toast.success(`¡Logro desbloqueado: ${def.name}!`, {
                  description: def.description,
                  duration: 5000,
                });
              }
            }
          });
        }

        set({ 
          achievements: newAchievements,
          unlockedBadgeIds: data.unlockedBadgeIds || [],
          unlockedTitleIds: data.unlockedTitleIds || [],
          activeTitleId: data.activeTitleId || null,
          isLoading: false,
          isInitialized: true
        });
      } else {
        set({ isLoading: false, isInitialized: true });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, `users/${userId}/achievements/state`);
      set({ isLoading: false });
    }
  },

  evaluateAchievements: async (userId) => {
    if (!userId) return;
    
    const now = Date.now();
    const { lastEvaluated } = get();
    
    // Throttle evaluation to once every 5 minutes unless explicitly forced
    if (now - lastEvaluated < 1000 * 60 * 5) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[AchievementStore] Skipping evaluation (throttled)');
      }
      return;
    }

    const user = useAuthStore.getState().user;
    await evaluateAchievements(userId, { profile: user });
    set({ lastEvaluated: now });
    await get().fetchAchievements(userId);
  },

  claimAchievement: async (userId, achievementId) => {
    const achievements = { ...get().achievements };
    if (achievements[achievementId]) {
      achievements[achievementId].claimed = true;
      try {
        await setDoc(doc(db, 'users', userId, 'achievements', 'state'), {
          achievements
        }, { merge: true });
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, `users/${userId}/achievements/state`);
      }
    }
  },

  setActiveTitle: async (userId, titleId) => {
    try {
      await setDoc(doc(db, 'users', userId, 'achievements', 'state'), {
        activeTitleId: titleId
      }, { merge: true });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `users/${userId}/achievements/state`);
    }
  },

  resetAchievements: async (userId: string) => {
    try {
      const achievementsRef = doc(db, 'users', userId, 'achievements', 'state');
      const initialState: PlayerAchievementState = {
        achievements: {},
        unlockedBadgeIds: [],
        unlockedTitleIds: [],
        activeTitleId: null
      };
      await setDoc(achievementsRef, initialState);
      set({ 
        achievements: {},
        unlockedBadgeIds: [],
        unlockedTitleIds: [],
        activeTitleId: null,
        isInitialized: true
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `users/${userId}/achievements/state`);
      throw error;
    }
  }
}));
